/**
 * Proofroom — Main Entry Point
 * Per PLAN.md src/main.js
 */

import './index.css';

// Core
import { Canvas } from './core/Canvas.js';
import { eventBus, Events } from './core/EventBus.js';
import { History, CreateNodeCommand, DeleteNodeCommand, MoveNodesCommand, UpdateNodeCommand, CreateConnectionCommand, DeleteConnectionCommand, GroupNodesCommand, UngroupNodesCommand } from './core/History.js';

// Models
import { Node } from './models/Node.js';
import { Connection } from './models/Connection.js';
import { Group } from './models/Group.js';
import { CanvasDocument } from './models/CanvasDocument.js';

// Interactions
import { SelectTool } from './interactions/SelectTool.js';
import { ConnectTool } from './interactions/ConnectTool.js';
import { PanTool } from './interactions/PanTool.js';
import { NodeEditor } from './interactions/NodeEditor.js';

// UI
import { Toolbar } from './ui/Toolbar.js';
import { SideEditor } from './ui/SideEditor.js';
import { ContextMenu } from './ui/ContextMenu.js';
import { MiniMap } from './ui/MiniMap.js';

// Storage
import { Store } from './storage/Store.js';
import { Exporter } from './storage/Exporter.js';

// Constants
import { TOOL, THEME, SHORTCUTS, GRID_SIZE } from './utils/constants.js';
import { debounce } from './utils/debounce.js';

/**
 * Proofroom Application
 */
class ProofroomApp {
    constructor() {
        // Core components
        this.canvas = null;
        this.document = null;
        this.history = new History();
        this.store = new Store();

        // Tools
        this.selectTool = null;
        this.connectTool = null;
        this.panTool = null;
        this.nodeEditor = null;
        this.currentTool = TOOL.SELECT;

        // UI
        this.toolbar = null;
        this.sideEditor = null;
        this.contextMenu = null;
        this.miniMap = null;
        this.exporter = null;

        // Auto-save debounced
        this.autoSave = debounce(() => this.saveDocument(), 1000);

        this.init();
    }

    async init() {
        // Wait for DOM
        if (document.readyState === 'loading') {
            await new Promise(resolve => document.addEventListener('DOMContentLoaded', resolve));
        }

        // Get elements
        const canvasElement = document.getElementById('proofroom-canvas');
        const container = document.getElementById('canvas-container');
        const toolbarContainer = document.getElementById('toolbar-container');
        const editorContainer = document.getElementById('side-editor-container');
        const contextContainer = document.getElementById('context-menu-container');
        const minimapContainer = document.getElementById('minimap-container');

        // Initialize canvas
        this.canvas = new Canvas(canvasElement, container);

        // Load or create document
        await this.loadOrCreateDocument();

        // Initialize tools
        this.selectTool = new SelectTool(this.canvas, this.document);
        this.connectTool = new ConnectTool(this.canvas, this.document);
        this.panTool = new PanTool(this.canvas);
        this.nodeEditor = new NodeEditor(this.document, this.canvas);

        // Initialize UI
        this.toolbar = new Toolbar(toolbarContainer);
        this.sideEditor = new SideEditor(editorContainer);
        this.contextMenu = new ContextMenu(contextContainer);
        this.miniMap = new MiniMap(minimapContainer, this.canvas);
        this.exporter = new Exporter(this.canvas, this.document);

        // Set up event handlers
        this.bindEvents();

        // Apply theme
        const theme = await this.store.theme();
        this.setTheme(theme);

        // Initial render
        eventBus.emit(Events.RENDER_REQUEST);

        console.log('Proofroom initialized');
    }

    async loadOrCreateDocument() {
        // Check for existing document
        const currentId = await this.store.currentDocument();

        if (currentId) {
            const doc = await this.store.loadDocument(currentId);
            if (doc) {
                this.document = doc;
                this.canvas.setDocument(this.document);
                return;
            }
        }

        // Create new document
        this.document = new CanvasDocument('Untitled Canvas');
        this.canvas.setDocument(this.document);
        await this.store.currentDocument(this.document.id);
        await this.saveDocument();
    }

    async saveDocument() {
        if (this.document) {
            // Update viewport state
            this.document.viewport = this.canvas.viewport.toJSON();
            await this.store.saveDocument(this.document);
        }
    }

    bindEvents() {
        // Tool changes
        eventBus.on(Events.TOOL_CHANGE, ({ tool }) => {
            this.setTool(tool);
        });

        // Node creation (from double-click or toolbar)
        eventBus.on(Events.NODE_CREATE_REQUEST, ({ x, y, type }) => {
            this.createNode(x, y, type || 'latex');
        });

        // Toolbar actions
        eventBus.on('toolbar:add', () => {
            const center = this.canvas.viewport.screenToWorld(
                this.canvas.viewport.width / 2,
                this.canvas.viewport.height / 2
            );
            this.createNode(center.x, center.y, 'latex');
        });

        eventBus.on('toolbar:grid', () => {
            this.document.toggleGrid();
        });

        eventBus.on('toolbar:zoomIn', () => {
            this.canvas.viewport.setZoom(this.canvas.viewport.zoom * 1.2);
        });

        eventBus.on('toolbar:zoomOut', () => {
            this.canvas.viewport.setZoom(this.canvas.viewport.zoom / 1.2);
        });

        eventBus.on('toolbar:theme', () => {
            const newTheme = this.canvas.isDark ? THEME.LIGHT : THEME.DARK;
            this.setTheme(newTheme);
        });

        eventBus.on('toolbar:erase', () => {
            if (confirm('Erase all nodes? This cannot be undone.')) {
                this.document.clear();
                this.canvas.clearSelection();
                this.nodeEditor.cancel();
                this.canvas.markNodesChanged();
            }
        });

        // Editor events
        this.sideEditor.onContentChange = (content) => {
            this.nodeEditor.setContent(content);
        };

        this.sideEditor.onTypeChange = (type) => {
            this.nodeEditor.setType(type);
        };

        this.sideEditor.onConfirm = () => {
            const result = this.nodeEditor.confirm();
            if (result && result.oldContent !== result.newContent) {
                // Create undo command for content change
                this.history.execute(new UpdateNodeCommand(
                    this.document,
                    result.nodeId,
                    result.newContent,
                    result.oldContent
                ));
            }
            this.autoSave();
        };

        this.sideEditor.onCancel = () => {
            this.nodeEditor.cancel();
        };

        // Context menu actions
        eventBus.on('contextmenu:action', ({ action, nodeId, connectionId, worldPos }) => {
            this.handleContextAction(action, nodeId, connectionId, worldPos);
        });

        // Right-click handling
        document.addEventListener('contextmenu', (e) => {
            e.preventDefault();
            const worldPos = this.canvas.viewport.screenToWorld(e.clientX, e.clientY);
            const node = this.document.getNodeAtPosition(worldPos.x, worldPos.y);

            if (node) {
                this.contextMenu.showForNode(node.id, e.clientX, e.clientY);
            } else {
                this.contextMenu.showForCanvas(e.clientX, e.clientY, worldPos);
            }
        });

        // Keyboard shortcuts
        document.addEventListener('keydown', (e) => {
            this.handleKeyboard(e);
        });

        // Node drag handling on overlay
        this.setupNodeDragHandling();

        // Auto-save on changes
        eventBus.on(Events.NODE_CREATE, () => this.autoSave());
        eventBus.on(Events.NODE_DELETE, () => this.autoSave());
        eventBus.on(Events.NODE_MOVE, () => this.autoSave());
        eventBus.on(Events.CONNECTION_CREATE, () => this.autoSave());
        eventBus.on(Events.CONNECTION_DELETE, () => this.autoSave());
    }

    setupNodeDragHandling() {
        const overlay = document.getElementById('node-overlay');
        if (!overlay) return;

        let isDragging = false;
        let isConnecting = false;
        let startX, startY;
        let draggedNodeId = null;

        overlay.addEventListener('mousedown', (e) => {
            const nodeEl = e.target.closest('.node');
            if (!nodeEl) return;

            draggedNodeId = nodeEl.dataset.nodeId;

            // If connect tool is active, start connection
            if (this.currentTool === TOOL.CONNECT) {
                isConnecting = true;
                this.connectTool.startConnection(draggedNodeId, e.clientX, e.clientY);
                return;
            }

            // Select node if not already selected
            if (!this.canvas.selectedNodeIds.has(draggedNodeId)) {
                if (!e.shiftKey) {
                    this.canvas.selectNodes([draggedNodeId]);
                } else {
                    this.canvas.selectedNodeIds.add(draggedNodeId);
                }
            }

            isDragging = true;
            startX = e.clientX;
            startY = e.clientY;

            this.selectTool.startDrag(e.clientX, e.clientY);
        });

        document.addEventListener('mousemove', (e) => {
            if (isConnecting) {
                this.connectTool.updateConnection(e.clientX, e.clientY, e.altKey, e.shiftKey, e.metaKey);
                return;
            }
            if (!isDragging) return;
            this.selectTool.doDrag(e.clientX, e.clientY);
        });

        document.addEventListener('mouseup', (e) => {
            if (isConnecting) {
                isConnecting = false;
                const connection = this.connectTool.endConnection(e.clientX, e.clientY);
                if (connection) {
                    this.history.execute(new CreateConnectionCommand(this.document, connection));
                }
                draggedNodeId = null;
                return;
            }

            if (!isDragging) return;
            isDragging = false;

            const result = this.selectTool.endDrag();
            if (result && (result.deltaX !== 0 || result.deltaY !== 0)) {
                // Record move for undo
                eventBus.emit(Events.NODE_MOVE, result);
            }

            draggedNodeId = null;
        });

        // Double-click to edit (and switch to select mode if in arrow mode)
        overlay.addEventListener('dblclick', (e) => {
            const nodeEl = e.target.closest('.node');
            if (nodeEl) {
                // Switch back to select mode if in connect mode
                if (this.currentTool === TOOL.CONNECT) {
                    this.setTool(TOOL.SELECT);
                }
                eventBus.emit(Events.EDITOR_OPEN, { nodeId: nodeEl.dataset.nodeId });
            }
        });
    }

    createNode(x, y, type) {
        const node = new Node(type, '', x, y);

        this.history.execute(new CreateNodeCommand(this.document, node));

        this.canvas.selectNodes([node.id]);

        // Open editor immediately
        setTimeout(() => {
            eventBus.emit(Events.EDITOR_OPEN, { nodeId: node.id });
        }, 100);
    }

    deleteSelectedNodes() {
        for (const nodeId of this.canvas.selectedNodeIds) {
            this.history.execute(new DeleteNodeCommand(this.document, nodeId));
        }
        this.canvas.clearSelection();
    }

    duplicateSelectedNodes() {
        const newIds = [];

        for (const nodeId of this.canvas.selectedNodeIds) {
            const node = this.document.getNode(nodeId);
            if (node) {
                const clone = node.clone();
                this.history.execute(new CreateNodeCommand(this.document, clone));
                newIds.push(clone.id);
            }
        }

        this.canvas.selectNodes(newIds);
    }

    groupSelectedNodes() {
        const nodeIds = Array.from(this.canvas.selectedNodeIds);
        if (nodeIds.length < 2) return;

        const group = new Group(nodeIds);
        this.history.execute(new GroupNodesCommand(this.document, group));
    }

    ungroupSelectedNodes() {
        for (const nodeId of this.canvas.selectedNodeIds) {
            const node = this.document.getNode(nodeId);
            if (node?.groupId) {
                this.history.execute(new UngroupNodesCommand(this.document, node.groupId));
                break; // Only ungroup once per action
            }
        }
    }

    setTool(tool) {
        this.currentTool = tool;

        // Deactivate all tools
        this.selectTool.setActive(false);
        this.connectTool.setActive(false);
        this.panTool.setActive(false);

        // Activate selected tool
        switch (tool) {
            case TOOL.SELECT:
                this.selectTool.setActive(true);
                break;
            case TOOL.CONNECT:
                this.connectTool.setActive(true);
                break;
            case TOOL.PAN:
                this.panTool.setActive(true);
                break;
        }
    }

    setTheme(theme) {
        document.documentElement.setAttribute('data-theme', theme);
        this.document.setTheme(theme);
        this.canvas.isDark = theme === THEME.DARK;
        this.store.theme(theme);
        eventBus.emit(Events.RENDER_REQUEST);
    }

    handleContextAction(action, nodeId, connectionId, worldPos) {
        switch (action) {
            case 'edit':
                eventBus.emit(Events.EDITOR_OPEN, { nodeId });
                break;
            case 'duplicate':
                if (nodeId) {
                    this.canvas.selectNodes([nodeId]);
                    this.duplicateSelectedNodes();
                }
                break;
            case 'group':
                this.groupSelectedNodes();
                break;
            case 'copyLatex':
                const node = this.document.getNode(nodeId);
                if (node) {
                    navigator.clipboard.writeText(node.content);
                }
                break;
            case 'delete':
                if (nodeId) {
                    this.canvas.selectNodes([nodeId]);
                    this.deleteSelectedNodes();
                }
                break;
            case 'addEquation':
                this.createNode(worldPos.worldX, worldPos.worldY, 'latex');
                break;
            case 'addText':
                this.createNode(worldPos.worldX, worldPos.worldY, 'text');
                break;
            case 'zoomFit':
                this.canvas.viewport.fitToContent(this.document.getAllNodes());
                break;
            case 'deleteConnection':
                if (connectionId) {
                    this.history.execute(new DeleteConnectionCommand(this.document, connectionId));
                }
                break;
        }
    }

    handleKeyboard(e) {
        // Ignore if typing in input
        if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') {
            return;
        }

        const isMeta = e.metaKey || e.ctrlKey;
        const isShift = e.shiftKey;

        switch (e.key.toLowerCase()) {
            case SHORTCUTS.NEW_NODE:
                if (!isMeta) {
                    e.preventDefault();
                    const center = this.canvas.viewport.screenToWorld(
                        this.canvas.viewport.width / 2,
                        this.canvas.viewport.height / 2
                    );
                    this.createNode(center.x, center.y, 'latex');
                }
                break;

            case SHORTCUTS.SELECT_TOOL:
                if (!isMeta) {
                    e.preventDefault();
                    this.toolbar.setTool(TOOL.SELECT);
                }
                break;

            case SHORTCUTS.CONNECT_TOOL:
                if (!isMeta) {
                    e.preventDefault();
                    this.toolbar.setTool(TOOL.CONNECT);
                }
                break;

            case SHORTCUTS.TOGGLE_GRID:
                if (isMeta && !isShift) {
                    e.preventDefault();
                    this.document.toggleGrid();
                } else if (isMeta && isShift) {
                    e.preventDefault();
                    this.ungroupSelectedNodes();
                } else if (!isMeta) {
                    e.preventDefault();
                    this.document.toggleGrid();
                }
                break;

            case SHORTCUTS.DUPLICATE:
                if (isMeta) {
                    e.preventDefault();
                    this.duplicateSelectedNodes();
                }
                break;

            case SHORTCUTS.DELETE:
            case 'backspace':
                if (!isMeta) {
                    e.preventDefault();
                    this.deleteSelectedNodes();
                }
                break;

            case SHORTCUTS.UNDO:
                if (isMeta && !isShift) {
                    e.preventDefault();
                    this.history.undo();
                } else if (isMeta && isShift) {
                    e.preventDefault();
                    this.history.redo();
                }
                break;

            case SHORTCUTS.ZOOM_IN:
            case '+':
                if (isMeta) {
                    e.preventDefault();
                    this.canvas.viewport.setZoom(this.canvas.viewport.zoom * 1.2);
                }
                break;

            case SHORTCUTS.ZOOM_OUT:
                if (isMeta) {
                    e.preventDefault();
                    this.canvas.viewport.setZoom(this.canvas.viewport.zoom / 1.2);
                }
                break;

            case SHORTCUTS.ZOOM_FIT:
                if (isMeta) {
                    e.preventDefault();
                    this.canvas.viewport.fitToContent(this.document.getAllNodes());
                }
                break;

            case SHORTCUTS.ESCAPE:
                e.preventDefault();
                this.canvas.clearSelection();
                if (this.sideEditor.getIsVisible()) {
                    this.nodeEditor.cancel();
                }
                break;
        }
    }
}

// Start the app
const app = new ProofroomApp();

// Expose for debugging
window.proofroom = app;
